import { getValidAccessToken } from './getValidAccessToken';
import { normalizeEnvKey } from './envKeyUtils';
import { decodeJwtParts } from './debug/jwtDebug';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getFunctionsUrl = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  if (!supabaseUrl) throw new Error('Missing REACT_APP_SUPABASE_URL');
  return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/leaderboard`;
};

const buildAuthHeaders = async () => {
  const headers = { 'Content-Type': 'application/json' };

  const jwtAnonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_JWT_ANON_KEY);
  const anonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_ANON_KEY);

  const jwtAnonDecoded = decodeJwtParts(jwtAnonRaw);
  const jwtAnonIsUsableJwt =
    jwtAnonDecoded?.partsCount === 3 &&
    !jwtAnonDecoded?.decodeError &&
    jwtAnonDecoded?.payload;

  const anonDecoded = decodeJwtParts(anonRaw);
  const anonLooksJwt =
    anonDecoded?.partsCount === 3 &&
    !anonDecoded?.decodeError &&
    anonDecoded?.payload;

  const apikeyToSend = jwtAnonIsUsableJwt
    ? jwtAnonRaw
    : anonLooksJwt
      ? anonRaw
      : null;

  const accessToken = await getValidAccessToken();
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (apikeyToSend) headers.apikey = apikeyToSend;

  return headers;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetch leaderboard rankings for a specific game, metric, and time period.
 *
 * @param {{ gameType: string, metric: string, period: string }} params
 * @returns {Promise<{ rankings: Array<{ rank, username, score, gamesPlayed }>, myRank: object|null }>}
 */
export const fetchLeaderboard = async ({ gameType, metric, period }) => {
  const headers = await buildAuthHeaders();
  if (!headers.Authorization) {
    const err = new Error('Sign in to view leaderboards.');
    err.code = 'UNAUTHORIZED';
    throw err;
  }

  let response = await fetch(getFunctionsUrl(), {
    method: 'POST',
    headers,
    body: JSON.stringify({ game_type: gameType, metric, period }),
  });

  // Retry once on 401
  if (response.status === 401) {
    const refreshed = await getValidAccessToken({ forceRefresh: true });
    if (refreshed) {
      const retryHeaders = { ...headers, Authorization: `Bearer ${refreshed}` };
      response = await fetch(getFunctionsUrl(), {
        method: 'POST',
        headers: retryHeaders,
        body: JSON.stringify({ game_type: gameType, metric, period }),
      });
    }
  }

  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!response.ok) {
    const err = new Error(data?.error || text || 'Failed to load leaderboard.');
    err.code = data?.code;
    err.status = response.status;
    throw err;
  }

  return {
    rankings: Array.isArray(data?.rankings) ? data.rankings.map((r) => ({
      rank: r.rank,
      userId: r.user_id,
      username: r.username || 'Anonymous',
      score: r.score,
      gamesPlayed: r.games_played,
    })) : [],
    myRank: data?.myRank ? {
      rank: data.myRank.rank,
      userId: data.myRank.user_id,
      username: data.myRank.username || 'Anonymous',
      score: data.myRank.score,
      gamesPlayed: data.myRank.games_played,
    } : null,
  };
};
